/**
 * Lead Detail Page
 * Shows full lead information with related widgets:
 * - Account/Contact (linked)
 * - Tasks (open, due soon)
 * - Activities (recent)
 * - Quotes (related)
 * - Orders (if converted)
 * - Quick Add bar for common actions
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { crmAPI, quotesAPI, adminAPI } from '../../utils/api';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

// Import shared components from the Unified Detail Page System
import { 
  DetailHeader, 
  DetailPageLayout, 
  ContentPanel, 
  ActionBar,
  RelatedOrdersTable,
  Widget,
  WidgetItem,
  WidgetTaskItem,
  WidgetOrderItem,
  WidgetActivityItem
} from '../../components/detail-system';
import {
  User,
  Phone,
  Mail,
  Building2,
  FileText,
  Calendar,
  CheckCircle,
  ClipboardList,
  ShoppingCart,
  Activity,
  Plus,
  Loader2,
  TrendingUp,
  DollarSign,
  Clock,
  AlertTriangle,
  Flag,
  Zap
} from 'lucide-react';

// Activity types for Quick Add
const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'email', label: 'Email' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'note', label: 'Note' }
];

// Task priorities
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'med', label: 'Medium' },
  { value: 'high', label: 'High' }
];

// Lead stages with colors
const LEAD_STAGES = [
  { value: 'new', label: 'New', color: 'blue' },
  { value: 'attempted_contact', label: 'Attempted Contact', color: 'yellow' },
  { value: 'in_conversation', label: 'In Conversation', color: 'purple' },
  { value: 'quoted', label: 'Quoted', color: 'purple' },
  { value: 'negotiation', label: 'Negotiation', color: 'yellow' },
  { value: 'won', label: 'Won', color: 'green' },
  { value: 'lost', label: 'Lost', color: 'red' },
  { value: 'dormant', label: 'Dormant', color: 'default' }
];

export default function LeadDetailPage({ 
  leadId, 
  onBack, 
  onNavigateToCustomer,
  onNavigateToContact,
  onNavigateToOrder,
  onNavigateToQuote,
  onNavigateToTask
}) {
  const navigate = useNavigate();
  
  // Main data
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Related data
  const [account, setAccount] = useState(null);
  const [contact, setContact] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  
  // Widget loading states
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  
  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  
  // Form data
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due_at: '',
    priority: 'med'
  });
  const [activityForm, setActivityForm] = useState({
    type: 'call',
    subject: '',
    body: '',
    occurred_at: new Date().toISOString().slice(0, 16)
  });
  const [selectedStage, setSelectedStage] = useState('');
  
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);

  // Fetch lead with related data
  const fetchLead = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await crmAPI.getLead(leadId);
      setLead(data.lead);
      setAccount(data.account);
      setContact(data.contact);
      setTasks(data.tasks || []);
      setActivities(data.activities || []);
      setSelectedStage(data.lead?.stage || '');
    } catch (err) {
      console.error('Failed to fetch lead:', err);
      setError('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  // Fetch related quotes (via account)
  const fetchQuotes = useCallback(async () => {
    if (!lead?.account_id) return;
    try {
      setLoadingQuotes(true);
      const data = await quotesAPI.getQuotes({ account_id: lead.account_id, limit: 10 });
      setQuotes(data.quotes || []);
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
    } finally {
      setLoadingQuotes(false);
    }
  }, [lead?.account_id]);

  // Fetch related orders (via account)
  const fetchOrders = useCallback(async () => {
    if (!lead?.account_id) return;
    try {
      setLoadingOrders(true);
      const data = await adminAPI.getOrders({ account_id: lead.account_id, limit: 10 });
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  }, [lead?.account_id]);

  // Fetch tasks for widget refresh
  const fetchTasks = useCallback(async () => {
    try {
      setLoadingTasks(true);
      const data = await crmAPI.getTasks({ lead_id: leadId, status: 'open', limit: 5 });
      setTasks(data.tasks || []);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  }, [leadId]);

  // Fetch activities for widget refresh
  const fetchActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const data = await crmAPI.getActivities({ lead_id: leadId, limit: 10 });
      setActivities(data.activities || []);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  }, [leadId]);

  // Fetch users for owner assignment
  const fetchUsers = useCallback(async () => {
    try {
      const data = await adminAPI.getUsers({ limit: 100 });
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    if (leadId) {
      fetchLead();
      fetchUsers();
    }
  }, [leadId, fetchLead, fetchUsers]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchLead();
      if (lead?.account_id) {
        await Promise.all([fetchQuotes(), fetchOrders()]);
      }
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchLead,
    enabled: !loading && !!leadId,
    refreshing,
    throttleMs: 1500,
  });

  // Load quotes/orders once lead is loaded
  useEffect(() => {
    if (lead?.account_id) {
      fetchQuotes();
      fetchOrders();
    }
  }, [lead?.account_id, fetchQuotes, fetchOrders]);

  // Handle task creation
  const handleCreateTask = async () => {
    if (!taskForm.title.trim() || !taskForm.due_at) {
      alert('Title and due date are required');
      return;
    }
    
    try {
      setSaving(true);
      await crmAPI.createTask({
        ...taskForm,
        lead_id: leadId,
        account_id: lead?.account_id || null,
        due_at: new Date(taskForm.due_at).toISOString()
      });
      setShowTaskModal(false);
      setTaskForm({ title: '', description: '', due_at: '', priority: 'med' });
      fetchTasks();
    } catch (err) {
      console.error('Failed to create task:', err);
      alert('Failed to create task: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle activity logging
  const handleLogActivity = async () => {
    if (!activityForm.type) {
      alert('Activity type is required');
      return;
    }
    
    try {
      setSaving(true);
      await crmAPI.createActivity({
        ...activityForm,
        lead_id: leadId,
        account_id: lead?.account_id || null,
        contact_id: lead?.contact_id || null,
        occurred_at: new Date(activityForm.occurred_at).toISOString()
      });
      setShowActivityModal(false);
      setActivityForm({ type: 'call', subject: '', body: '', occurred_at: new Date().toISOString().slice(0, 16) });
      fetchActivities();
    } catch (err) {
      console.error('Failed to log activity:', err);
      alert('Failed to log activity: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle stage update
  const handleUpdateStage = async () => {
    try {
      setSaving(true);
      await crmAPI.updateLead(leadId, { stage: selectedStage });
      setShowStageModal(false);
      fetchLead();
    } catch (err) {
      console.error('Failed to update stage:', err);
      alert('Failed to update stage: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle task completion
  const handleCompleteTask = async (taskId) => {
    try {
      await crmAPI.completeTask(taskId);
      fetchTasks();
    } catch (err) {
      console.error('Failed to complete task:', err);
    }
  };

  // Get stage color
  const getStageColor = (stage) => {
    const found = LEAD_STAGES.find(s => s.value === stage);
    return found?.color || 'default';
  };

  // Get stage label
  const getStageLabel = (stage) => {
    const found = LEAD_STAGES.find(s => s.value === stage);
    return found?.label || stage?.replace('_', ' ');
  };

  // Check if follow-up is overdue
  const isOverdue = lead?.next_followup_at && new Date(lead.next_followup_at) < new Date();

  // Quick Actions
  const quickActions = [
    {
      id: 'create-quote',
      label: 'Create Quote',
      icon: FileText,
      onClick: () => {
        if (lead?.account_id) {
          onNavigateToQuote?.({ create: true, account_id: lead.account_id });
        } else {
          alert('Link this lead to a customer first to create a quote.');
        }
      }
    },
    {
      id: 'create-task',
      label: 'Create Task',
      icon: ClipboardList,
      onClick: () => setShowTaskModal(true)
    },
    {
      id: 'create-order',
      label: 'Create Order',
      icon: ShoppingCart,
      onClick: () => {
        if (lead?.account_id) {
          onNavigateToOrder?.({ create: true, account_id: lead.account_id });
        } else {
          alert('Link this lead to a customer first to create an order.');
        }
      }
    },
    {
      id: 'log-activity',
      label: 'Log Activity',
      icon: Activity,
      onClick: () => setShowActivityModal(true)
    },
    {
      id: 'change-stage',
      label: 'Update Stage',
      icon: TrendingUp,
      onClick: () => setShowStageModal(true)
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="lead-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="flex flex-col items-center justify-center h-96" data-testid="lead-detail-error">
        <p className="text-red-600 mb-4">{error || 'Lead not found'}</p>
        <Button onClick={onBack}>Go Back</Button>
      </div>
    );
  }

  const statusBadges = [];
  if (lead.rush) {
    statusBadges.push({ label: 'RUSH', color: 'red' });
  }
  if (lead.priority === 'high') {
    statusBadges.push({ label: 'High Priority', color: 'red' });
  }
  if (isOverdue) {
    statusBadges.push({ label: 'Overdue', color: 'red' });
  }

  // Lead Stage Status Config
  const LEAD_STATUS_CONFIG = {
    new: { label: 'New', color: 'bg-gray-100 text-gray-700' },
    qualified: { label: 'Qualified', color: 'bg-blue-100 text-blue-700' },
    proposal: { label: 'Proposal', color: 'bg-purple-100 text-purple-700' },
    negotiation: { label: 'Negotiation', color: 'bg-yellow-100 text-yellow-700' },
    won: { label: 'Won', color: 'bg-green-100 text-green-700' },
    lost: { label: 'Lost', color: 'bg-red-100 text-red-700' }
  };

  // Header Actions
  const headerPrimaryActions = [
    {
      label: 'Create Quote',
      icon: FileText,
      onClick: () => {
        if (lead?.account_id) {
          onNavigateToQuote?.({ create: true, account_id: lead.account_id });
        } else {
          alert('Link this lead to a customer first to create a quote.');
        }
      },
      testId: 'create-quote-btn'
    }
  ];

  // Quick Actions for ActionBar
  const quickActionsForBar = quickActions.map(action => ({
    ...action,
    testId: `quick-action-${action.id}`
  }));

  // Sidebar content
  const sidebarContent = (
    <>
      {/* Open Tasks Widget */}
      <Widget
        title="Open Tasks"
        icon={ClipboardList}
        count={tasks.filter(t => t.status === 'open').length}
        loading={loadingTasks}
        onAdd={() => setShowTaskModal(true)}
        addLabel="Add"
        onViewAll={tasks.length > 0 ? () => navigate(`/admin?tab=tasks&lead_id=${leadId}&lead_name=${encodeURIComponent(lead?.id || leadId)}`) : undefined}
        emptyMessage="No open tasks"
        emptyAction={{
          label: 'Create Task',
          icon: Plus,
          onClick: () => setShowTaskModal(true)
        }}
      >
        {tasks.filter(t => t.status === 'open').slice(0, 5).map((task) => (
          <WidgetTaskItem
            key={task.id}
            title={task.title}
            dueDate={task.due_at}
            priority={task.priority}
            status={task.status}
            onComplete={() => handleCompleteTask(task.id)}
            onClick={() => onNavigateToTask?.(task.id)}
          />
        ))}
      </Widget>

      {/* Recent Activity Widget */}
      <Widget
        title="Recent Activity"
        icon={Activity}
        count={activities.length}
        loading={loadingActivities}
        onAdd={() => setShowActivityModal(true)}
        addLabel="Log"
        emptyMessage="No activity logged"
        emptyAction={{
          label: 'Log Activity',
          icon: Plus,
          onClick: () => setShowActivityModal(true)
        }}
      >
        {activities.slice(0, 5).map((activity) => (
          <WidgetActivityItem
            key={activity.id}
            type={activity.type}
            subject={activity.subject}
            body={activity.body}
            occurredAt={activity.occurred_at}
            createdBy={activity.created_by_user_id}
          />
        ))}
      </Widget>
    </>
  );

  return (
    <>
    <DetailPageLayout
      headerProps={{
        entityName: lead.id,
        subtitle: `${lead.source?.replace('_', ' ')} • Created ${new Date(lead.created_at).toLocaleDateString()}`,
        entityIcon: TrendingUp,
        status: lead.stage,
        statusConfig: LEAD_STATUS_CONFIG,
        statusEditable: true,
        onStatusClick: () => setShowStageModal(true),
        onBack,
        backLabel: 'Back',
        onRefresh: handleRefresh,
        refreshing: refreshing,
        primaryActions: headerPrimaryActions,
        testId: 'lead-detail-header'
      }}
      sidebarContent={sidebarContent}
      testId="lead-detail-page"
    >
      {/* Quick Actions */}
      <ActionBar actions={quickActionsForBar} />

      {/* Lead Overview Card */}
      <ContentPanel title="Lead Overview" icon={TrendingUp} testId="lead-overview-panel">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                getStageColor(lead.stage) === 'green' ? 'bg-green-100 text-green-800' :
                getStageColor(lead.stage) === 'red' ? 'bg-red-100 text-red-800' :
                getStageColor(lead.stage) === 'blue' ? 'bg-blue-100 text-blue-800' :
                getStageColor(lead.stage) === 'purple' ? 'bg-purple-100 text-purple-800' :
                getStageColor(lead.stage) === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {getStageLabel(lead.stage)}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowStageModal(true)}
                data-testid="change-stage-btn"
              >
                Change Stage
              </Button>
            </div>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Owner</p>
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-gray-400" />
              <p className="text-sm font-medium text-gray-900">{lead.owner_user_id || 'Unassigned'}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Source</p>
            <p className="text-sm font-medium text-gray-900">{lead.source?.replace('_', ' ')}</p>
          </div>
          
          <div className="col-span-2">
            <div className={`flex items-center gap-2 p-3 rounded-lg ${isOverdue ? 'bg-red-50 border border-red-200' : 'bg-blue-50'}`}>
              <Clock className={`w-4 h-4 ${isOverdue ? 'text-red-600' : 'text-blue-600'}`} />
              <div>
                <p className={`text-xs ${isOverdue ? 'text-red-600' : 'text-blue-600'} font-medium`}>
                  {isOverdue ? 'OVERDUE' : 'Next Follow-up'}
                </p>
                <p className={`text-sm font-medium ${isOverdue ? 'text-red-800' : 'text-blue-800'}`}>
                  {lead.next_followup_at ? new Date(lead.next_followup_at).toLocaleString() : 'Not set'}
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Estimated Value</p>
            <div className="flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5 text-gray-400" />
              <p className="text-sm font-medium text-gray-900">{lead.estimated_value ? `$${lead.estimated_value.toLocaleString()}` : 'Not set'}</p>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Category</p>
            <p className="text-sm font-medium text-gray-900">{lead.category?.replace('_', ' ') || 'Not set'}</p>
          </div>
          
          <div>
            <p className="text-xs text-gray-500 mb-1">Priority</p>
            <div className="flex items-center gap-2">
              <Flag className="w-3.5 h-3.5 text-gray-400" />
              <p className="text-sm font-medium text-gray-900">{lead.priority?.toUpperCase() || 'MED'}</p>
            </div>
          </div>
          {lead.rush && (
            <div className="flex items-center gap-2 text-red-600">
              <Zap className="w-4 h-4" />
              <span className="text-sm font-medium">RUSH ORDER</span>
            </div>
          )}
          
          {lead.expected_close_date && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Expected Close</p>
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <p className="text-sm font-medium text-gray-900">{new Date(lead.expected_close_date).toLocaleDateString()}</p>
              </div>
            </div>
          )}
        </div>
      </ContentPanel>

      {/* Account/Contact Card */}
      <ContentPanel title="Account & Contact" icon={Building2} testId="account-contact-panel">
        <div className="space-y-4">
          {/* Account */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Customer</p>
                <p className="font-medium text-gray-900">
                  {account?.name || 'No customer linked'}
                </p>
              </div>
            </div>
            {account && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onNavigateToCustomer?.(account.id)}
                data-testid="view-customer-btn"
              >
                View
              </Button>
            )}
          </div>
          
          {/* Contact */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Contact</p>
                <p className="font-medium text-gray-900">
                  {contact 
                    ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed'
                    : 'No contact linked'}
                </p>
                {contact?.email && (
                  <p className="text-xs text-gray-500">{contact.email}</p>
                )}
              </div>
            </div>
            {contact && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onNavigateToContact?.(contact.id)}
                data-testid="view-contact-btn"
              >
                View
              </Button>
            )}
          </div>
        </div>
      </ContentPanel>

      {/* Notes Card */}
      {lead.notes && (
        <ContentPanel title="Notes" icon={FileText} testId="notes-panel">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.notes}</p>
        </ContentPanel>
      )}

      {/* Quotes Widget - Main area */}
      <Widget
        title="Quotes"
        icon={FileText}
        count={quotes.length}
        loading={loadingQuotes}
        onAdd={() => {
          if (lead?.account_id) {
            onNavigateToQuote?.({ create: true, account_id: lead.account_id });
          }
        }}
        addLabel="Add"
        onViewAll={quotes.length > 0 ? () => navigate(`/admin?tab=orders-quote-mode&lead_id=${leadId}&lead_name=${encodeURIComponent(lead?.id || leadId)}`) : undefined}
        emptyMessage={lead.account_id ? "No quotes yet" : "Link to customer to see quotes"}
      >
        {quotes.slice(0, 5).map((quote) => (
          <WidgetItem
            key={quote.id}
            title={quote.quote_number || quote.id}
            subtitle={`$${(quote.totals?.grand_total || quote.total || 0).toLocaleString()}`}
            badge={quote.status}
            badgeColor={quote.status === 'approved' ? 'green' : quote.status === 'sent' ? 'blue' : 'default'}
            onClick={() => onNavigateToQuote?.(quote.id)}
          />
        ))}
      </Widget>

      {/* Orders Widget - Using shared RelatedOrdersTable */}
      <RelatedOrdersTable
        orders={orders.map(o => ({
          ...o,
          order_id: o.order_id || o.id,
          created_at: o.created_at,
          ops_status: o.ops_status || o.status,
          invoice_status: o.invoice_status
        }))}
        total={orders.length}
        onViewOrder={(orderId) => onNavigateToOrder?.(orderId)}
        onViewAll={orders.length > 0 ? () => navigate(`/admin?tab=orders&lead_id=${leadId}&lead_name=${encodeURIComponent(lead?.id || leadId)}`) : undefined}
        showCreateButton={false}
        loading={loadingOrders}
        maxItems={5}
        emptyMessage={lead.account_id ? "No orders yet" : "Link to customer to see orders"}
        testIdPrefix="lead-orders"
      />
    </DetailPageLayout>

    {/* Create Task Modal */}
    <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
      <DialogContent className="max-w-md" data-testid="create-task-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Create Task for Lead {lead.id}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Task Title *</Label>
            <Input
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              placeholder="Enter task title..."
              data-testid="task-title-input"
            />
          </div>
          
          <div>
            <Label>Description</Label>
            <Textarea
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              placeholder="Add details..."
              rows={3}
              data-testid="task-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Due Date *</Label>
              <Input
                type="datetime-local"
                value={taskForm.due_at}
                onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value })}
                data-testid="task-due-input"
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateTask} 
              disabled={saving || !taskForm.title || !taskForm.due_at}
              data-testid="save-task-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent className="max-w-md" data-testid="log-activity-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Log Activity for Lead {lead.id}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Activity Type *</Label>
              <Select
                value={activityForm.type}
                onValueChange={(v) => setActivityForm({ ...activityForm, type: v })}
              >
                <SelectTrigger data-testid="activity-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Subject</Label>
              <Input
                value={activityForm.subject}
                onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })}
                placeholder="Brief summary..."
                data-testid="activity-subject-input"
              />
            </div>
            
            <div>
              <Label>Details</Label>
              <Textarea
                value={activityForm.body}
                onChange={(e) => setActivityForm({ ...activityForm, body: e.target.value })}
                placeholder="Add notes..."
                rows={3}
                data-testid="activity-body-input"
              />
            </div>
            
            <div>
              <Label>When</Label>
              <Input
                type="datetime-local"
                value={activityForm.occurred_at}
                onChange={(e) => setActivityForm({ ...activityForm, occurred_at: e.target.value })}
                data-testid="activity-datetime-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityModal(false)}>Cancel</Button>
            <Button 
              onClick={handleLogActivity} 
              disabled={saving}
              data-testid="save-activity-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Log Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Stage Modal */}
      <Dialog open={showStageModal} onOpenChange={setShowStageModal}>
        <DialogContent className="max-w-sm" data-testid="change-stage-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Update Lead Stage
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>New Stage</Label>
            <Select
              value={selectedStage}
              onValueChange={setSelectedStage}
            >
              <SelectTrigger data-testid="stage-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEAD_STAGES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageModal(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateStage} 
              disabled={saving || !selectedStage}
              data-testid="save-stage-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
